import { notification } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import { JobPostingDetail } from '../types';
import { buildCandidateQueryParam, buildRecruitmentProcessQueryParam } from '../utils';
import { EmailFormValues } from './EmailForm';
import SendEmailModal, { SendEmailModalProps } from './SendEmailModal';

interface BulkSendEmailModalProps extends Omit<SendEmailModalProps, 'helmetTitle'> {
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (data: void, variables: EmailFormValues, context: unknown) => void | Promise<unknown>;
  functionId?: JobPostingDetail['function_id'];
  totalCount?: number;
}

const BulkSendEmailModal = ({
  onSuccess,
  jobPostingId,
  functionId,
  totalCount,
  ...props
}: BulkSendEmailModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const mutation = useAPIMutation(
    async (
      { message, attachments, subject, fromName, replyTo, enableReplyTo }: EmailFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      if (jobPostingId) {
        await api.post(
          '/recruitment-processes-actions/send-email-to-all/',
          {
            from_name: fromName,
            job_posting: jobPostingId,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'COMMUNICATION',
            attachments: attachmentIds,
          },
          {
            params: await buildRecruitmentProcessQueryParam(
              searchParams,
              api,
              jobPostingId,
              functionId
            ),
          }
        );
      } else {
        await api.post(
          '/candidates-actions/send-email-to-all/',
          {
            from_name: fromName,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'COMMUNICATION',
            attachments: attachmentIds,
          },
          {
            params: buildCandidateQueryParam(searchParams),
          }
        );
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.error === 'empty_queryset_send_email_to_all') {
          return notification.error({
            duration: 0,
            message: t('Erro'),
            description: t(
              'Não é possível realizar esta ação, pois não há candidatos a serem selecionados. Recarregue a página e tente novamente.'
            ),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  return (
    <SendEmailModal
      title={
        <span>
          <Trans>Enviar e-mail para</Trans>{' '}
          <strong>
            {totalCount ?? '...'} <Trans>candidato(s)</Trans>
          </strong>
        </span>
      }
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      helmetTitle={t(`Enviar e-mail para {{totalCount}} candidato(s)`, {
        totalCount: totalCount ?? '...',
      })}
      loading={mutation.isLoading}
      jobPostingId={jobPostingId}
      {...props}
    />
  );
};

export default BulkSendEmailModal;
